import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ContactFormForm from "../../modules/ContactFormSection";
import { clearEmptyCampaignData } from "../../common/campaign-data-setter";
import type { Swiper as SwiperType } from "swiper";
import regulations from "./assets/Regulamin-promocji-Taniej-z-mBankiem-3-edycja.pdf";
import "./index.scss";

import logoMauto from "./assets/logo-mauto.svg";
import iconArrow from "./assets/ikona-strzalka.svg";
import heroAuto from "./assets/hero-auto.png";
import chooserAuto from "./assets/chooser-auto-n.png";
import iconCheckGreen from "./assets/ikona-check-green.png";
import iconCheckRed from "./assets/ikona-check-red.png";
import iconInfo from "./assets/ikona-info.png";
import benefitsCars from "./assets/auta.png";
import { Button, Stack, Stripe, Text, UnorderedList } from "@mbank-design/design-system/components";
import HorizontalRadioBox from "../../parts/HorizontalRadioBox";
import phoneImage from "./assets/phoneImage.png";
import officeIcon from "./assets/office.svg";
import phoneIconAsset from "./assets/phone_icon.svg";
import laptopIcon from "./assets/laptop_icon.svg";
import ringIcon from "./assets/ring_icon.svg";
import { IconList } from "../../parts/IconList";
import { TickRound } from "@mbank-design/design-system/icons";
import { FontColor } from "@mbank-design/design-system/design-tokens/general/typography";

type BenefitCard = {
  modifier: string;
  title: string;
  items: string[];
  isClone?: boolean;
};

const BENEFIT_CARDS: BenefitCard[] = [
  {
    modifier: "1",
    title: "konto firmowe za\u00A00\u00A0zł",
    items: ["0\u00A0zł za\u00A0prowadzenie rachunku firmowego", "zarządzaj wygodnie finansami w\u00A0jednym miejscu"]
  },
  {
    modifier: "2",
    title: "oszczędność i\u00A0prostota dla\u00A0Twojej firmy",
    items: [
      "2000\u00A0zł korzyści",
      "finansowanie i\u00A0rachunek w\u00A0jednym banku – mniej formalności, więcej kontroli"
    ]
  },
  {
    modifier: "3",
    title: "szybka procedura",
    items: ["wszystko możesz załatwić zdalnie – wybierasz auto, podpisujesz umowę, korzystasz z\u00A0promocji"]
  },
  {
    modifier: "4",
    title: "wsparcie ekspertów",
    items: ["nasi doradcy są do\u00A0Twojej dyspozycji na\u00A0każdym etapie"]
  }
];

type SwiperBundle = typeof import("swiper").Swiper;

let swiperBundlePromise: Promise<SwiperBundle> | null = null;

const loadSwiperBundle = async (): Promise<SwiperBundle> => {
  if (!swiperBundlePromise) {
    swiperBundlePromise = Promise.all([import("swiper/bundle"), import("swiper/css/bundle")]).then(
      ([swiperModule]) => swiperModule.default
    );
  }

  return swiperBundlePromise;
};

const BtnArrow = () => (
  <img className="btn__arrow" src={iconArrow} alt="" aria-hidden="true" width={28} height={28} />
);

const ExpandableText = ({
  collapsedLabel = "rozwiń",
  expandedLabel = "zwiń",
  children
}: {
  collapsedLabel?: string;
  expandedLabel?: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);

  const toggle = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((value) => !value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      toggle(event);
    }
  };

  return (
    <>
      <span
        className="open-hidden-text"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        {open ? expandedLabel : collapsedLabel}
      </span>
      <span className={`hidden-text${open ? " is-open" : ""}`}>{children}</span>
    </>
  );
};

const BenefitCardSlide = ({ card, isMobile }: { card: BenefitCard; isMobile: boolean }) => (
  <li
    className={`benefit-card benefit-card--${card.modifier}${card.isClone ? " benefit-card--clone" : ""}${isMobile ? " swiper-slide" : ""
      }`}
    aria-hidden={card.isClone ? true : undefined}
  >
    <h3 className="benefit-card__title">{card.title}</h3>
    <ul className="benefit-card__list">
      {card.items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </li>
);

const TaniejZMb = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeBenefitDot, setActiveBenefitDot] = useState(0);
  const [isMobileBenefits, setIsMobileBenefits] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 991px)").matches : false
  );

  const headerRef = useRef<HTMLElement>(null);
  const benefitsSwiperRef = useRef<HTMLDivElement>(null);
  const swiperInstanceRef = useRef<SwiperType | null>(null);

  const closeMenu = useCallback(() => {
    setIsNavOpen(false);
  }, []);

  useEffect(() => {
    clearEmptyCampaignData();
  }, []);

  useEffect(() => {
    const onKeyUp = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    const onResize = () => {
      if (window.innerWidth >= 992) {
        closeMenu();
      }
    };

    document.addEventListener("keyup", onKeyUp);
    window.addEventListener("resize", onResize);

    return () => {
      document.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("resize", onResize);
    };
  }, [closeMenu]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991px)");
    const updateViewport = () => setIsMobileBenefits(mq.matches);

    updateViewport();
    mq.addEventListener("change", updateViewport);

    return () => mq.removeEventListener("change", updateViewport);
  }, []);

  useEffect(() => {
    if (!isMobileBenefits) {
      return undefined;
    }

    let mounted = true;
    let frameId = 0;

    const destroySwiper = () => {
      if (swiperInstanceRef.current) {
        swiperInstanceRef.current.destroy(true, true);
        swiperInstanceRef.current = null;
      }
    };

    const syncDots = (swiper: SwiperType) => {
      setActiveBenefitDot(swiper.realIndex % BENEFIT_CARDS.length);
    };

    const initSwiper = async () => {
      const benefitsEl = benefitsSwiperRef.current;

      if (!benefitsEl || !mounted || swiperInstanceRef.current) {
        return;
      }

      try {
        const Swiper = await loadSwiperBundle();

        if (!mounted || swiperInstanceRef.current || !benefitsSwiperRef.current) {
          return;
        }

        swiperInstanceRef.current = new Swiper(benefitsSwiperRef.current, {
          slidesPerView: 1.3,
          centeredSlides: true,
          spaceBetween: 16,
          loop: true,
          autoHeight: true,
          watchOverflow: true,
          breakpoints: {
            576: { slidesPerView: 1.55, spaceBetween: 20 },
            768: { slidesPerView: 1.85, spaceBetween: 24 }
          },
          on: {
            init: syncDots,
            slideChange: syncDots
          }
        });
      } catch (error) {
        console.error("Failed to initialize benefits Swiper", error);
      }
    };

    frameId = window.requestAnimationFrame(() => {
      initSwiper();
    });

    return () => {
      mounted = false;
      window.cancelAnimationFrame(frameId);
      destroySwiper();
    };
  }, [isMobileBenefits]);

  const toggleNav = () => {
    setIsNavOpen((value) => !value);
  };

  const goToBenefit = (index: number) => {
    swiperInstanceRef.current?.slideToLoop(index);
  };

  const benefitSlides = isMobileBenefits
    ? [...BENEFIT_CARDS, ...BENEFIT_CARDS.map((card) => ({ ...card, isClone: true }))]
    : BENEFIT_CARDS;

  return (
    <div className="page taniej-z-mb">
      <Helmet>
        <title>mAuto – sfinansuj samochód i zyskaj 2000 zł</title>
        <meta
          name="description"
          content="Odbierz bonus na leasing z kontem firmowym w mBanku. Sprawdź warunki promocji, wybierz auto z oferty mAuto i ciesz się niższą ratą leasingu lub najmu."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="mAuto – sfinansuj samochód i zyskaj 2000 zł" />
        <meta
          property="og:description"
          content="Odbierz bonus na leasing z kontem firmowym w mBanku. Sprawdź warunki promocji, wybierz auto z oferty mAuto i ciesz się niższą ratą leasingu lub najmu."
        />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <header ref={headerRef} className={`site-header${isNavOpen ? " is-open" : ""}`} id="top">
        <div className="container site-header__inner">
          <Link className="site-header__logo" to="/" aria-label="mAuto – strona główna">
            <img src={logoMauto} alt="mAuto" width={196} height={64} />
          </Link>
          <button
            className="nav-toggle"
            type="button"
            aria-label={isNavOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={isNavOpen}
            aria-controls="menu"
            onClick={toggleNav}
          >
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
          </button>
          <nav className="main-nav" id="menu" aria-label="Menu główne">
            <ul className="main-nav__list">
              <li>
                <a className="main-nav__link" href="#warunki" onClick={closeMenu}>
                  zasady promocji
                </a>
              </li>
              <li>
                <a className="main-nav__link" href="#dlaczego-warto" onClick={closeMenu}>
                  dlaczego warto?
                </a>
              </li>
              <li>
                <a className="main-nav__link" href="#jak-skorzystac" onClick={closeMenu}>
                  jak skorzystać z&nbsp;promocji?
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero" id="hero">
          <div className="container hero__inner">
            <div className="hero__intro">
              {/* <p className="hero__eyebrow">korzystny zwrot akcji z&nbsp;mBankiem</p> */}
              <h1 className="hero__title">
                sfinansuj samochód, spłacaj leasing z&nbsp;konta firmowego w&nbsp;mBanku
                <span className="hero__title-accent">i&nbsp;zyskaj 2000&nbsp;zł</span>
              </h1>
              {!isMobileBenefits ? (
                <p className="hero__lead">
                  Teraz z&nbsp;rachunkiem firmowym w&nbsp;mBanku <strong>zyskujesz jeszcze więcej!</strong>
                </p>) :
                (<>
                  <br />
                  <br />
                </>)
              }
            </div>
          </div>
        </section>

        <section className="terms-blue" id="warunki">
          <div className="container terms-blue__inner">
            <h2 className="terms-blue__title">
              wystarczy, że&nbsp;spełnisz
              <br />
              <span className="terms-blue__title-accent">2 proste warunki:</span>
            </h2>
            <ol className="terms-blue__steps">
              <li className="num-step">
                <div className="num-step__head">
                  <span className="num-step__num">1</span>
                  <span className="num-step__label">krok</span>
                </div>
                <p className="num-step__text">
                  spłacisz co&nbsp;najmniej <strong>dwie z&nbsp;trzech pierwszych rat leasingowych</strong> z&nbsp;rachunku
                  firmowego w&nbsp;mBanku
                </p>
              </li>
              <li className="num-step">
                <div className="num-step__head">
                  <span className="num-step__num">2</span>
                  <span className="num-step__label">krok</span>
                </div>
                <p className="num-step__text">
                  <strong>opłacaj pełne raty w&nbsp;terminie</strong>
                </p>
              </li>
            </ol>
            <a className="btn btn--accent terms-blue__cta" href="#formularz">
              zapytaj o&nbsp;ofertę
              <BtnArrow />
            </a>
          </div>
        </section>

        <section className="chooser" id="wybor-auta">
          <div className="container">
            <h2 className="section-title chooser__title">
              jeśli nie&nbsp;wybrałeś jeszcze auta,
              <br />
              możesz je&nbsp;<span className="chooser__title-accent">wybrać z&nbsp;naszej oferty mAuto</span>
            </h2>
            {!isMobileBenefits && (<p className="chooser__subtitle">
              Masz dwie możliwości. Wybierz opcję, która jest dla&nbsp;Ciebie najwygodniejsza
            </p>)}

            <div className="chooser__grid">
              <article className="choice-card choice-card--mauto">
                <div className="choice-card__body">
                  <div className="choice-card__text">
                    <h3 className="choice-card__title">
                      wybierz auto
                      <br />
                      na <img className="choice-card__logo" src={logoMauto} alt="mAuto" width={150} height={49} />
                    </h3>
                    <p className="choice-card__desc">
                      Znajdź samochód z&nbsp;naszej oferty i&nbsp;od&nbsp;razu złóż wniosek o&nbsp;leasing
                    </p>
                    <ul className="choice-card__list">
                      <li>
                        <img
                          className="choice-card__check"
                          src={iconCheckGreen}
                          alt=""
                          aria-hidden="true"
                          width={28}
                          height={28}
                        />
                        auta dostępne od&nbsp;ręki
                      </li>
                      <li>
                        <img
                          className="choice-card__check"
                          src={iconCheckGreen}
                          alt=""
                          aria-hidden="true"
                          width={28}
                          height={28}
                        />
                        finansowanie w&nbsp;jednym miejscu
                      </li>
                    </ul>
                  </div>
                  <figure className="choice-card__media" aria-hidden="true">
                    <img src={chooserAuto} alt="" width={739} height={548} />
                  </figure>
                </div>
                <Link className="btn btn--green choice-card__cta" to="/lista-pojazdow">
                  wybierz na&nbsp;mAuto <BtnArrow />
                </Link>
              </article>

              {!isMobileBenefits && (<>
                <div className="chooser__or" aria-hidden="true">
                  <span>lub</span>
                </div>

                <article className="choice-card choice-card--own">
                  <h3 className="choice-card__title">masz już wybrane auto?</h3>
                  <p className="choice-card__desc">
                    Jeśli znalazłeś samochód u&nbsp;dealera lub na&nbsp;rynku, wypełnij formularz
                  </p>
                  <ul className="choice-card__list">
                    <li>
                      <img
                        className="choice-card__check"
                        src={iconCheckRed}
                        alt=""
                        aria-hidden="true"
                        width={28}
                        height={28}
                      />
                      nowe i&nbsp;używane pojazdy
                    </li>
                    <li>
                      <img
                        className="choice-card__check"
                        src={iconCheckRed}
                        alt=""
                        aria-hidden="true"
                        width={28}
                        height={28}
                      />
                      oferta przygotowana indywidualnie
                    </li>
                  </ul>
                  <a className="btn btn--accent choice-card__cta" href="#formularz">
                    wypełnij formularz <BtnArrow />
                  </a>
                </article>
              </>
              )}
            </div>

            {!isMobileBenefits && <p className="chooser__note">
              <img src={iconInfo} alt="" aria-hidden="true" width={24} height={24} />
              <div>
                Finansowanie tylko w&nbsp;PLN.
                <br />
                Promocja dotyczy leasingu pojazdów o&nbsp;wartości co&nbsp;najmniej 100&nbsp;000&nbsp;zł netto. Jeśli nie masz
                jeszcze rachunku firmowego w&nbsp;mBanku, możesz go założyć szybko i&nbsp;wygodnie przez Twojego opiekuna
                handlowego. Szczegóły promocji w&nbsp;
                <a href={regulations} target="_blank" rel="noopener noreferrer">
                  regulaminie
                </a>
                .
              </div>
            </p>}
          </div>
        </section>

        <section className="lead" id="formularz">
          <div className="container lead__inner">
            <h2 className="lead__title">
              Jeżeli masz już wybrany samochód,
              <br />
              <strong>wypełnij formularz i&nbsp;otrzymaj propozycję finansowania</strong>
            </h2>
            <div className="lead__form">
              <ContactFormForm isDefaultContact />
            </div>
            {isMobileBenefits && <p className="chooser__note">
              <img src={iconInfo} alt="" aria-hidden="true" width={24} height={24} />
              <div>
                Finansowanie tylko w&nbsp;PLN.
                <br />
                Promocja dotyczy leasingu pojazdów o&nbsp;wartości co&nbsp;najmniej 100&nbsp;000&nbsp;zł netto. Jeśli nie masz
                jeszcze rachunku firmowego w&nbsp;mBanku, możesz go założyć szybko i&nbsp;wygodnie przez Twojego opiekuna
                handlowego. Szczegóły promocji w&nbsp;
                <a href={regulations} target="_blank" rel="noopener noreferrer">
                  regulaminie
                </a>
                .
              </div>
            </p>}
          </div>
        </section>

        <section className="benefits" id="dlaczego-warto">
          <div className="container">
            <h2 className="section-title benefits__title">dlaczego warto</h2>
            <div className="benefits__layout">
              <div className="benefits__visual" aria-hidden="true">
                <span className="benefits__ring" />
                <img className="benefits__cars" src={benefitsCars} alt="" />
              </div>
              <div
                className={`benefits__cards${isMobileBenefits ? " swiper" : ""}`}
                ref={isMobileBenefits ? benefitsSwiperRef : null}
              >
                <ul className={isMobileBenefits ? "swiper-wrapper" : "benefits__cards-list"}>
                  {benefitSlides.map((card, index) => (
                    <BenefitCardSlide
                      key={`${card.modifier}-${card.isClone ? "clone" : "main"}-${index}`}
                      card={card}
                      isMobile={isMobileBenefits}
                    />
                  ))}
                </ul>
              </div>
              {isMobileBenefits && (
                <div className="benefits__dots" role="tablist" aria-label="Wybierz korzyść">
                  {BENEFIT_CARDS.map((card, index) => (
                    <button
                      key={card.modifier}
                      className={`benefits__dot${activeBenefitDot === index ? " is-active" : ""}`}
                      type="button"
                      aria-label={`Korzyść ${index + 1}`}
                      onClick={() => goToBenefit(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="promo-steps" id="jak-skorzystac">
          <div className="container">
            <h2 className="section-title promo-steps__title">jak skorzystać z&nbsp;promocji?</h2>
            <ol className="promo-steps__list">
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">1</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  <strong>załóż rachunek firmowy</strong> przez Twojego doradcę leasingowego, jeśli nie&nbsp;jesteś
                  jeszcze klientem mBanku
                </p>
              </li>
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">2</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  <strong>wybierz auto i&nbsp;podpisz umowę leasingu lub&nbsp;najmu</strong> na&nbsp;wybrane auto albo
                  auto z&nbsp;oferty mAuto
                </p>
              </li>
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">3</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  <strong>spłać 2&nbsp;z&nbsp;3 pierwszych rat</strong> z&nbsp;konta firmowego w&nbsp;mBanku
                </p>
              </li>
              <li className="promo-step">
                <div className="promo-step__head">
                  <span className="promo-step__num">4</span>
                  <span className="promo-step__label">krok</span>
                </div>
                <p className="promo-step__text">
                  w&nbsp;szóstym miesiącu Twojej umowy{" "}
                  <strong>dostaniesz przelew w&nbsp;wysokości 2000&nbsp;zł od&nbsp;mLeasing!</strong>
                </p>
              </li>
            </ol>
            <a className="btn btn--accent promo-steps__cta" href="#formularz">
              zapytaj o&nbsp;ofertę <BtnArrow />
            </a>
            <div className="promo-steps__footer">
              <p className="promo-steps__regulamin">
                <a href={regulations} target="_blank" rel="noopener noreferrer">
                  Regulamin
                </a>
              </p>
              <p className="promo-steps__legal">
                Informacja jest informacją handlową i&nbsp;nie&nbsp;stanowi oferty w&nbsp;rozumieniu przepisów Kodeksu
                cywilnego. Ma charakter jedynie poglądowy i&nbsp;pomocniczy w&nbsp;przygotowaniu właściwej umowy. Umowa
                może być zawarta nie&nbsp;wcześniej niż
                po&nbsp;podjęciu przez mLeasing sp. z&nbsp;o.o. pozytywnej decyzji o&nbsp;finansowaniu. mAuto jest
                serwisem internetowym należącym do&nbsp;mLeasing sp. z&nbsp;o.o., stroną umowy będzie mLeasing sp.
                z&nbsp;o.o.
              </p>
            </div>
          </div>
        </section>
        <CompanySection />
      </main>


    </div>
  );
};

export default TaniejZMb;


type CompanyTypeOption = {
  Name: string;
  Value: string;
  Status: boolean;
};

const COMPANY_TYPE_OPTIONS: CompanyTypeOption[] = [
  { Name: "dla firm do 12 msc", Value: "upTo12Months", Status: true },
  { Name: "dla firm powyżej 12 msc", Value: "above12Months", Status: false }
];

const ZERO_ZL_HIGHLIGHT = '<span class="company-section__zero-zl">0 zł</span>';

const ADDITIONAL_BENEFITS_UP_TO_12_MONTHS = [
  `${ZERO_ZL_HIGHLIGHT} za przelewy internetowe w PLN i EUR oraz SWIFT i SORBNET`,
  `${ZERO_ZL_HIGHLIGHT} za przelewy ekspresowe (max 3 dziennie)`,
  `${ZERO_ZL_HIGHLIGHT} za bankomaty i wypłaty (w Polsce i za granicą)`,
  `${ZERO_ZL_HIGHLIGHT} wypłaty BLIK we wszystkich bankomatach krajowych`,
  `${ZERO_ZL_HIGHLIGHT} za kartę płatniczą`,
  `${ZERO_ZL_HIGHLIGHT} za raport MT940`,
  `za smartTerminal w aplikacji banku`,
  `0% prowizji dla transakcji do 200 tys brutto przez 2 lata oraz 0 zł za terminal`,
  `dostępny bezpośrednio w aplikacji mobilnej mBanku`,
  `zarządzanie historią transakcji, potwierdzeniami oraz zwrotami w jednym miejscu`
];
const ADDITIONAL_BENEFITS_ABOVE_12_MONTHS = [
  `${ZERO_ZL_HIGHLIGHT} za kartę do rachunku`,
  `pakiet 20 darmowych przelewów internetowych (ELIXIR i ekspresowe)`,
  `pakiet 20 darmowych przelewów internetowych w EUR (SEPA) do krajów Unii Europejskiej oraz Islandii, Norwegii, Lichtensteinu i Szwajcarii`,
  `${ZERO_ZL_HIGHLIGHT} za prowadzenie rachunku walutowego w EUR lub USD przez 2 lata (potem 10 zł miesięcznie)`,
  `możliwość otwarcia do 5 rachunków pomocniczych`,
  `${ZERO_ZL_HIGHLIGHT} za raport MT940`,
  `${ZERO_ZL_HIGHLIGHT} za smartTerminal oraz 0% prowizji dla transakcji do 200 tys brutto przez 2 lata`
];

type PaymentBenefitTile = {
  icon: string;
  title: string;
  items: string[];
};

const PAYMENT_BENEFIT_TILES: PaymentBenefitTile[] = [
  {
    icon: phoneIconAsset,
    title: "smartTerminal w aplikacji banku",
    items: [
      `Terminal POS lub aplikację płatniczą POSapp za ${ZERO_ZL_HIGHLIGHT} przez 24 miesiące, <strong>0% prowizji</strong> od transakcji przez max. 24 miesiące, kartami VISA i Mastercard do osiągnięcia łącznie kwoty 200 tysięcy zł brutto, ${ZERO_ZL_HIGHLIGHT} za instalację i aktywację Terminala POS.`
    ]
  },
  {
    icon: laptopIcon,
    title: "bramka płatności paynow dla sklepów internetowych",
    items: [
      "wystarczy jeden login i hasło – wszystko związane z bramką sprawdzisz po zalogowaniu się na konto firmowe",
      "<strong>0% prowizji</strong> dla transakcji do 200 tys zł brutto przez 2 lata (potem 0,95%)"
    ]
  },
  {
    icon: ringIcon,
    title: "pierścień płatniczy Mastercard",
    items: [
      "za otwarcie Rachunku bieżącego, jeśli na wniosku o Rachunek bieżący wybierzesz kartę debetową: Mastercard Debit Business, Mastercard Business WOŚP lub Mastercard Business WOŚP Mobilna i w ciągu 30 dni zrobisz nią transakcje bezgotówkowe na łączną kwotę min. 100 złotych."
    ]
  }
];

const CompanyCardContent = ({ isUpTo12Months }: { isUpTo12Months: boolean }) => (
  <>
    <div className="company-section__content-main">
      <span className="company-section__office-icon">
        <img src={officeIcon} alt="" aria-hidden="true" width={30} height={30} />
      </span>
      {isUpTo12Months ? (
        <h4 className="company-section__content-title">
          otwórz konto firmowe
          <br />
          z pakietem płatności na start
        </h4>
      ) : (
        <h4 className="company-section__content-title">
          otwórz konto firmowe
          <br />
          z rozwiązaniami
          <br />
          dla biznesu
        </h4>
      )}
      <p className="company-section__content-desc">
        <b>Zyskaj bezterminowo konto firmowe za 0 zł</b>  – wystarczy przelew do ZUS lub wpływ na min. 1000 zł miesięcznie
      </p>
    </div>
    <div className="company-section__additional-list">
      <IconList
        header="Dodatkowo:"
        headerType="h3"
        textColor={FontColor.DARK}
        icon={TickRound}
        iconColor="positive_contrast"
        content={!isUpTo12Months ? ADDITIONAL_BENEFITS_UP_TO_12_MONTHS : ADDITIONAL_BENEFITS_ABOVE_12_MONTHS}
        marginBottom={16}
        itemWidth="80%"
      />
    </div>
  </>
);

const CompanySection = () => {
  const [companyTypeOptions, setCompanyTypeOptions] = useState(COMPANY_TYPE_OPTIONS);
  const isUpTo12Months = companyTypeOptions.find((option) => option.Status)?.Value === "upTo12Months";
  const [contentCardMinHeight, setContentCardMinHeight] = useState<number>();
  const measureUpTo12MonthsRef = useRef<HTMLDivElement>(null);
  const measureAbove12MonthsRef = useRef<HTMLDivElement>(null);

  const updateContentCardMinHeight = useCallback(() => {
    if (window.innerWidth <= 1023) {
      setContentCardMinHeight(undefined);
      return;
    }

    const heights = [measureUpTo12MonthsRef, measureAbove12MonthsRef].map(
      (ref) => ref.current?.offsetHeight ?? 0
    );

    setContentCardMinHeight(Math.max(...heights));
  }, []);

  useLayoutEffect(() => {
    updateContentCardMinHeight();
    window.addEventListener("resize", updateContentCardMinHeight);

    return () => window.removeEventListener("resize", updateContentCardMinHeight);
  }, [updateContentCardMinHeight]);

  const handleCompanyTypeChange = ({ Value }: { Value: string }) => {
    setCompanyTypeOptions((options) =>
      options.map((option) => ({
        ...option,
        Status: option.Value === Value
      }))
    );
  };

  return (
    <section className="company-section">
      <div className="container">
        <div className="company-section__intro">
          <div className="company-section__switcher">
            <HorizontalRadioBox
              RadioName="companyType"
              Options={companyTypeOptions}
              handler={handleCompanyTypeChange}
              Size="normal"
            />
          </div>

          <header className="company-section__header">
            <h2 className="section-title company-section__title">
              otwórz konto firmowe i <span className="company-section__title-accent">zyskaj więcej</span>
            </h2>
            <h3 className="company-section__subtitle">Dwa rozwiązania dopasowane do etapu rozwoju Twojej firmy</h3>
          </header>
        </div>

        <div className="company-section__layout">
          <div className="company-section__content-measure" aria-hidden="true">
            <div ref={measureUpTo12MonthsRef} className="company-section__content-card-inner">
              <CompanyCardContent isUpTo12Months={true} />
            </div>
            <div ref={measureAbove12MonthsRef} className="company-section__content-card-inner">
              <CompanyCardContent isUpTo12Months={false} />
            </div>
          </div>

          <div
            className="company-section__content-card"
            style={contentCardMinHeight ? { minHeight: contentCardMinHeight } : undefined}
          >
            <div className="company-section__content-card-inner">
              <CompanyCardContent isUpTo12Months={isUpTo12Months} />
            </div>

            {isUpTo12Months && <p className="company-section__disclaimer">
              Wskazane dodatkowe korzyści obowiązują przez 24 miesiące od otwarcia konta w promocji. Po 24 miesiącach
              otrzymasz bezterminowo pakiet 20 przelewów ELIXIR w PLN oraz pakiet 20 przelewów SEPA w EUR. Pierwsza
              wybrana karta debetowa oraz raport MT940 pozostaną za 0 zł. Pozostałe opłaty, w tym opłaty za bankomaty
              i wypłaty z bankomatów, będą zgodne z aktualnie obowiązującą Taryfą prowizji i opłat bankowych.
            </p>}

            <img
              src={phoneImage}
              alt=""
              aria-hidden="true"
              className="company-section__phone-image"
            />
          </div>
        </div>

        {isUpTo12Months && (
          <div className="company-section__benefits">
            <h3 className="company-section__benefits-title">
              z mBankiem <span className="company-section__title-accent">przyjmiesz płatności</span> tam, gdzie jest Twój
              klient
            </h3>
            <div className="company-section__benefits-grid">
              {PAYMENT_BENEFIT_TILES.map((tile) => (
                <article key={tile.title} className="company-section__benefit-tile">
                  <div className="company-section__benefit-tile-head">
                    <span className="company-section__benefit-tile-icon">
                      <img src={tile.icon} alt="" aria-hidden="true" />
                    </span>
                    <h4 className="company-section__benefit-tile-title">{tile.title}</h4>
                  </div>
                  <IconList
                    icon={TickRound}
                    iconColor="positive_contrast"
                    textColor={FontColor.DARK}
                    content={tile.items}
                    itemWidth="80%"
                  />
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};